import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DeletionBanner = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["profile-deletion", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("scheduled_deletion_at")
        .eq("user_id", user.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  const scheduledAt = profile?.scheduled_deletion_at;
  if (!scheduledAt) return null;

  const deletionDate = new Date(scheduledAt);
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((deletionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-deletion");
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Ștergerea contului a fost anulată!");
      queryClient.invalidateQueries({ queryKey: ["profile-deletion"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    } catch (err: any) {
      toast.error(err.message || "Eroare la anularea ștergerii");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mx-4 mt-4 sm:mx-6">
      <div className="flex items-center gap-3 min-w-0">
        <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
        <p className="text-sm text-destructive font-medium">
          Contul tău este programat pentru ștergere în <strong>{daysLeft} zile</strong> ({deletionDate.toLocaleDateString("ro-RO")}). 
          Datele tale vor fi șterse definitiv după această dată.
        </p>
      </div>
      <Button
        variant="outline"
        onClick={handleCancel}
        disabled={cancelling}
        className="shrink-0 w-full sm:w-auto h-12 font-semibold text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-200 hover:scale-[1.02] active:scale-95"
      >
        {cancelling ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <XCircle className="h-5 w-5 mr-2" />}
        Anulează ștergerea
      </Button>
    </div>
  );
};

export default DeletionBanner;
